import fs from 'node:fs';
import { isMainThread } from 'node:worker_threads';
import { LINE_CHUNK_SIZE, type ReadFileResult } from './types.js';

/** Files at or below this size use a single readFile + in-memory slice (fewer event-loop turns). */
export const SMALL_FILE_RANGE_MAX_BYTES = 512 * 1024;

function sliceLineRange(
  lines: string[],
  startLine: number,
  endLine: number,
  lineOffsetIn: number | undefined,
  lineChunkSize: number,
  fileSize: number,
): ReadFileResult {
  if (startLine === 0 && lineOffsetIn == null && lines[0] != null && lines[0].length > lineChunkSize) {
    return {
      content: lines[0].slice(0, lineChunkSize),
      size: fileSize,
      lineOffset: lineChunkSize,
      totalLines: lines.length,
    };
  }

  if (startLine === 0 && typeof lineOffsetIn === 'number') {
    const line0 = lines[0] ?? '';
    const from = lineOffsetIn;
    const take = Math.min(lineChunkSize, Math.max(0, line0.length - from));
    return {
      content: line0.slice(from, from + take),
      size: fileSize,
      ...(from + take < line0.length ? { lineOffset: from + take, totalLines: lines.length } : { totalLines: lines.length }),
    };
  }

  const slice = lines.slice(startLine, endLine + 1);
  return {
    content: slice.join('\n'),
    size: fileSize,
    totalLines: lines.length,
  };
}

/**
 * Synchronous small ranged read for interactive main-thread file browser requests.
 */
export function readSmallFileRangeSync(
  filePath: string,
  fileSize: number,
  startLine: number,
  endLine: number,
  lineOffsetIn?: number,
  lineChunkSize: number = LINE_CHUNK_SIZE,
): ReadFileResult {
  const raw = fs.readFileSync(filePath, 'utf8');
  const lines = raw.split(/\r?\n/);
  return sliceLineRange(lines, startLine, endLine, lineOffsetIn, lineChunkSize, fileSize);
}

/**
 * Fast path for small ranged reads: one read, then slice lines in memory.
 */
export async function readSmallFileRangeAsync(
  filePath: string,
  fileSize: number,
  startLine: number,
  endLine: number,
  lineOffsetIn?: number,
  lineChunkSize: number = LINE_CHUNK_SIZE,
): Promise<ReadFileResult> {
  if (isMainThread) {
    return readSmallFileRangeSync(filePath, fileSize, startLine, endLine, lineOffsetIn, lineChunkSize);
  }
  const raw = await fs.promises.readFile(filePath, 'utf8');
  const lines = raw.split(/\r?\n/);
  return sliceLineRange(lines, startLine, endLine, lineOffsetIn, lineChunkSize, fileSize);
}
